


import { Component, Prop, Vue } from "vue-property-decorator";

type Granularity = "day" | "week" | "month";
type Compare = "previous" | "year";

interface ChangePayload {
    From: string;
    To: string;
    Granularity: Granularity;
    Compare: Compare;
    Preset: string;
}

interface PresetDefinition {
    Key: string;
    Label: string;
}

interface CalendarDay {
    Date: Date;
    InMonth: boolean;
    Iso: string;
}

/* Preset keys in display order, each mapped to its i18n label key. The computation of
   From/To per preset lives in `presetRange` below (plain Date math, no libs). */
const PRESETS: PresetDefinition[] = [
    { Key: "today", Label: "HOME_rangeToday" },
    { Key: "last7Days", Label: "HOME_rangeLast7Days" },
    { Key: "last4Weeks", Label: "HOME_rangeLast4Weeks" },
    { Key: "last6Months", Label: "HOME_rangeLast6Months" },
    { Key: "last12Months", Label: "HOME_rangeLast12Months" },
    { Key: "currentMonth", Label: "HOME_rangeCurrentMonth" },
    { Key: "currentQuarter", Label: "HOME_rangeCurrentQuarter" },
    { Key: "yearToDate", Label: "HOME_rangeYearToDate" },
    { Key: "allTime", Label: "HOME_rangeAllTime" }
];

const GRANULARITIES: { Key: Granularity; Label: string }[] = [
    { Key: "day", Label: "HOME_granularityDay" },
    { Key: "week", Label: "HOME_granularityWeek" },
    { Key: "month", Label: "HOME_granularityMonth" }
];

const COMPARES: { Key: Compare; Label: string }[] = [
    { Key: "previous", Label: "HOME_comparePrevious" },
    { Key: "year", Label: "HOME_compareYear" }
];

/* FR Monday-first day initials, used as the calendar header. */
const WEEKDAY_INITIALS: string[] = [ "lu", "ma", "me", "je", "ve", "sa", "di" ];

@Component
export default class DashboardControls extends Vue {
    @Prop() From!: string;

    @Prop() To!: string;

    @Prop() Granularity!: Granularity;

    @Prop() Compare!: Compare;

    @Prop( { default: "yearToDate" } )
    Preset!: string;

    /* Which popover/dropdown is open ("range" | "granularity" | "compare" | ""). Only one at
       a time; outside clicks close them all. */
    private openControl: string = "";

    /* Calendar working selection. Driven by the "Début"/"Fin" inputs and by clicking days.
       Kept local until the user clicks "Appliquer". */
    private draftStart: string = "";
    private draftEnd: string = "";

    /* Preset key the draft currently reflects ("custom" once the user edits a day by hand).
       Seeded from the active preset when the popover opens; drives the preset-list highlight
       and the Preset emitted on "Appliquer". */
    private pendingPreset: string = "";

    /* Left-hand month shown in the dual-month calendar; the right month is this + 1. */
    private viewYear: number = new Date().getFullYear();
    private viewMonth: number = new Date().getMonth();

    private created(): void {
        window.addEventListener( "click", this.onOutsideClick );
    }

    private beforeDestroy(): void {
        window.removeEventListener( "click", this.onOutsideClick );
    }

    /* ----- Formatting / date helpers (no Date libs) ----------------------- */

    /* Date -> "YYYY-MM-DD" using LOCAL parts (avoids the UTC shift of toISOString). */
    private fmt( d: Date ): string {
        const year = d.getFullYear();
        const month = this.pad( d.getMonth() + 1 );
        const day = this.pad( d.getDate() );

        return year + "-" + month + "-" + day;
    }

    private pad( value: number ): string {
        return value < 10 ? "0" + value : String( value );
    }

    /* "YYYY-MM-DD" -> local Date at midnight. Empty/invalid -> null. */
    private parse( iso: string ): Date | null {
        if( iso === undefined || iso === "" )
            return null;

        const parts = iso.split( "-" );

        if( parts.length !== 3 )
            return null;

        const year = Number( parts[ 0 ] );
        const month = Number( parts[ 1 ] );
        const day = Number( parts[ 2 ] );

        if( isNaN( year ) || isNaN( month ) || isNaN( day ) )
            return null;

        return new Date( year, month - 1, day );
    }

    /* "YYYY-MM-DD" -> "31/12/2026" for the trigger label. Falls back to the raw value. */
    private displayDate( iso: string ): string {
        const date = this.parse( iso );

        if( date === null )
            return iso === undefined ? "" : iso;

        return this.pad( date.getDate() ) + "/"
            + this.pad( date.getMonth() + 1 ) + "/"
            + date.getFullYear();
    }

    /* ----- Preset computation -------------------------------------------- */

    private presetRange( key: string ): { From: string; To: string } {
        const today = new Date();
        const to = this.fmt( today );

        if( key === "today" )
            return { From: to, To: to };

        if( key === "last7Days" )
            return { From: this.fmt( this.addDays( today, -6 ) ), To: to };

        if( key === "last4Weeks" )
            return { From: this.fmt( this.addDays( today, -27 ) ), To: to };

        if( key === "last6Months" )
            return { From: this.fmt( this.firstOfMonth( today, -5 ) ), To: to };

        if( key === "last12Months" )
            return { From: this.fmt( this.firstOfMonth( today, -11 ) ), To: to };

        if( key === "currentMonth" )
            return { From: this.fmt( this.firstOfMonth( today, 0 ) ), To: to };

        if( key === "currentQuarter" ) {
            const quarterStartMonth = Math.floor( today.getMonth() / 3 ) * 3;
            const from = new Date( today.getFullYear(), quarterStartMonth, 1 );

            return { From: this.fmt( from ), To: to };
        }

        if( key === "yearToDate" )
            return { From: this.fmt( new Date( today.getFullYear(), 0, 1 ) ), To: to };

        if( key === "allTime" )
            return { From: "2015-01-01", To: to };

        return { From: this.From, To: this.To };
    }

    private addDays( base: Date, days: number ): Date {
        return new Date( base.getFullYear(), base.getMonth(), base.getDate() + days );
    }

    /* First day of the month `offset` months away from `base`. */
    private firstOfMonth( base: Date, offset: number ): Date {
        return new Date( base.getFullYear(), base.getMonth() + offset, 1 );
    }

    /* ----- Emit ----------------------------------------------------------- */

    /* Always emits the COMPLETE payload, overriding only the changed fields. */
    private emitChange( overrides: Partial<ChangePayload> ): void {
        const payload: ChangePayload = {
            From: overrides.From !== undefined ? overrides.From : this.From,
            To: overrides.To !== undefined ? overrides.To : this.To,
            Granularity: overrides.Granularity !== undefined
                ? overrides.Granularity
                : this.Granularity,
            Compare: overrides.Compare !== undefined ? overrides.Compare : this.Compare,
            Preset: overrides.Preset !== undefined ? overrides.Preset : this.Preset
        };

        this.$emit( "Change", payload );
    }

    /* ----- Open / close --------------------------------------------------- */

    private toggle( control: string ): void {
        if( this.openControl === control ) {
            this.openControl = "";
            return;
        }

        /* Seed the calendar draft + visible month from the active range when opening. */
        if( control === "range" )
            this.seedCalendar();

        this.openControl = control;
    }

    private seedCalendar(): void {
        this.draftStart = this.From === undefined ? "" : this.From;
        this.draftEnd = this.To === undefined ? "" : this.To;
        this.pendingPreset = this.Preset === undefined ? "custom" : this.Preset;

        const anchor = this.parse( this.draftStart );

        if( anchor !== null ) {
            this.viewYear = anchor.getFullYear();
            this.viewMonth = anchor.getMonth();
        }
    }

    /* Show the month containing the range END on the right pane, its previous month on the
       left — matches how Stripe frames the active range. */
    private focusCalendarOnEnd(): void {
        const end = this.parse( this.draftEnd );

        if( end === null )
            return;

        const left = this.firstOfMonth( end, -1 );

        this.viewYear = left.getFullYear();
        this.viewMonth = left.getMonth();
    }

    /* The component root is tagged with ref="root"; clicks outside it close everything. */
    private onOutsideClick( event: MouseEvent ): void {
        if( this.openControl === "" )
            return;

        const root = this.$refs.root as HTMLElement | undefined;

        if( root === undefined )
            return;

        if( !root.contains( event.target as Node ) )
            this.openControl = "";
    }

    /* ----- Trigger labels ------------------------------------------------- */

    private get rangeLabel(): string {
        const active = this.activePreset;

        if( active !== null )
            return this.$t( active.Label ) as string;

        return this.displayDate( this.From ) + " – " + this.displayDate( this.To );
    }

    private get activePreset(): PresetDefinition | null {
        for( const preset of PRESETS )
            if( preset.Key === this.Preset )
                return preset;

        return null;
    }

    private get granularityLabel(): string {
        for( const item of GRANULARITIES )
            if( item.Key === this.Granularity )
                return this.$t( item.Label ) as string;

        return "";
    }

    private get compareLabel(): string {
        for( const item of COMPARES )
            if( item.Key === this.Compare )
                return this.$t( item.Label ) as string;

        return "";
    }

    private get presets(): PresetDefinition[] {
        return PRESETS;
    }

    private get granularities(): { Key: Granularity; Label: string }[] {
        return GRANULARITIES;
    }

    private get compares(): { Key: Compare; Label: string }[] {
        return COMPARES;
    }

    private get weekdays(): string[] {
        return WEEKDAY_INITIALS;
    }

    /* ----- Control handlers ---------------------------------------------- */

    /* Clicking a preset pre-fills the Début/Fin inputs and the calendar selection, but keeps
       the popover open so the user can review (and tweak) before applying. */
    private selectPreset( preset: PresetDefinition ): void {
        const range = this.presetRange( preset.Key );

        this.draftStart = range.From;
        this.draftEnd = range.To;
        this.pendingPreset = preset.Key;

        this.focusCalendarOnEnd();
    }

    private selectGranularity( item: { Key: Granularity; Label: string } ): void {
        this.emitChange( { Granularity: item.Key } );

        this.openControl = "";
    }

    private selectCompare( item: { Key: Compare; Label: string } ): void {
        this.emitChange( { Compare: item.Key } );

        this.openControl = "";
    }

    /* ----- Calendar ------------------------------------------------------- */

    private get leftMonthLabel(): string {
        return this.monthTitle( this.viewYear, this.viewMonth );
    }

    private get rightMonthLabel(): string {
        const next = this.firstOfMonth( new Date( this.viewYear, this.viewMonth, 1 ), 1 );

        return this.monthTitle( next.getFullYear(), next.getMonth() );
    }

    private monthTitle( year: number, month: number ): string {
        const date = new Date( year, month, 1 );
        const label = date.toLocaleDateString( "fr-FR", { month: "long", year: "numeric" } );

        return label.charAt( 0 ).toUpperCase() + label.slice( 1 );
    }

    private get leftDays(): CalendarDay[] {
        return this.buildDays( this.viewYear, this.viewMonth );
    }

    private get rightDays(): CalendarDay[] {
        const next = this.firstOfMonth( new Date( this.viewYear, this.viewMonth, 1 ), 1 );

        return this.buildDays( next.getFullYear(), next.getMonth() );
    }

    /* Builds a 6-week (42-cell) Monday-first grid for the given month, padding with the
       trailing/leading days of the neighbouring months. */
    private buildDays( year: number, month: number ): CalendarDay[] {
        const first = new Date( year, month, 1 );

        /* getDay(): 0 = Sunday .. 6 = Saturday. Convert to Monday-first offset. */
        const offset = ( first.getDay() + 6 ) % 7;
        const start = new Date( year, month, 1 - offset );

        const days: CalendarDay[] = [];

        for( let i = 0; i < 42; ++i ) {
            const date = new Date( start.getFullYear(), start.getMonth(), start.getDate() + i );

            days.push( {
                Date: date,
                InMonth: date.getMonth() === month,
                Iso: this.fmt( date )
            } );
        }

        return days;
    }

    private prevMonth(): void {
        const target = this.firstOfMonth( new Date( this.viewYear, this.viewMonth, 1 ), -1 );

        this.viewYear = target.getFullYear();
        this.viewMonth = target.getMonth();
    }

    private nextMonth(): void {
        const target = this.firstOfMonth( new Date( this.viewYear, this.viewMonth, 1 ), 1 );

        this.viewYear = target.getFullYear();
        this.viewMonth = target.getMonth();
    }

    /* Clicking a day: with no start (or a full range) -> set start; with a start only ->
       set end, swapping if the user picked an earlier day. */
    private pickDay( day: CalendarDay ): void {
        /* Any manual day edit detaches the selection from its preset. */
        this.pendingPreset = "custom";

        if( this.draftStart === "" || this.draftEnd !== "" ) {
            this.draftStart = day.Iso;
            this.draftEnd = "";
            return;
        }

        if( day.Iso < this.draftStart ) {
            this.draftEnd = this.draftStart;
            this.draftStart = day.Iso;
            return;
        }

        this.draftEnd = day.Iso;
    }

    private isRangeStart( day: CalendarDay ): boolean {
        return this.draftStart !== "" && day.Iso === this.draftStart;
    }

    private isRangeEnd( day: CalendarDay ): boolean {
        return this.draftEnd !== "" && day.Iso === this.draftEnd;
    }

    private isInRange( day: CalendarDay ): boolean {
        if( this.draftStart === "" || this.draftEnd === "" )
            return false;

        return day.Iso > this.draftStart && day.Iso < this.draftEnd;
    }

    private clearRange(): void {
        this.draftStart = "";
        this.draftEnd = "";
        this.pendingPreset = "custom";
    }

    private get canApply(): boolean {
        return this.draftStart !== "" && this.draftEnd !== "";
    }

    private applyRange(): void {
        if( !this.canApply )
            return;

        const preset = this.pendingPreset === "" ? "custom" : this.pendingPreset;

        this.emitChange( { From: this.draftStart, To: this.draftEnd, Preset: preset } );

        this.openControl = "";
    }
}
